import { checkout as ImtblCheckout } from '@imtbl/sdk';
import { useCallback, useEffect, useState } from 'react';
import { useStore } from 'src/context/store-context';
import { useWidgets } from 'src/context/widgets-context';
import { WALLET_WIDGETS_ROOT, notifyError } from '../utils';

type NetworkInfo = Omit<ImtblCheckout.WalletNetworkSwitch, 'provider'>;

export const useWalletWidget = () => {
  const [
    {
      factory,
      checkout,
      provider,
      walletProviderName,
      bridgeWidget,
      swapWidget,
      onRampWidget,
      walletWidget
    },
    widgetsDispatch
  ] = useWidgets();
  const [{ walletOpen }, storeDispatch] = useStore();
  const [networkInfo, setNetworkInfo] = useState<NetworkInfo>({ chainId: 0, network: '' });

  const handleCloseWallet = useCallback(() => {
    storeDispatch({ payload: { type: 'SET_TOGGLE_WALLET_OPEN', open: false } });
    walletWidget?.unmount();
  }, [walletWidget, storeDispatch]);

  const handleOpenWallet = useCallback(() => {
    storeDispatch({ payload: { type: 'SET_TOGGLE_WALLET_OPEN', open: true } });
    walletWidget?.mount(WALLET_WIDGETS_ROOT);
  }, [walletWidget, storeDispatch]);

  const handleConnectionClose = useCallback(async () => {
    walletWidget?.unmount();
    storeDispatch({ payload: { type: 'SET_TOGGLE_WALLET_OPEN', open: false } });
    widgetsDispatch({
      payload: { type: 'SET_PROVIDER', provider: undefined, walletProviderName: undefined }
    });

    try {
      if (walletProviderName === ImtblCheckout.WalletProviderName.PASSPORT) {
        await checkout?.passport?.logout();
      }

      if (walletProviderName === ImtblCheckout.WalletProviderName.METAMASK) {
        await provider?.send('wallet_revokePermissions', [{ eth_accounts: {} }]);
      }
    } catch (err) {
      notifyError(err);
    }
  }, [checkout, provider, walletProviderName, walletWidget, widgetsDispatch, storeDispatch]);

  const handleNetworkSwitch = useCallback(
    ({ provider, chainId, network }: ImtblCheckout.WalletNetworkSwitch) => {
      setNetworkInfo({ chainId, network });
      widgetsDispatch({ payload: { type: 'SET_PROVIDER', provider, walletProviderName } });
    },
    [widgetsDispatch, walletProviderName]
  );

  useEffect(() => {
    // wallet widget events
    if (!walletWidget || !onRampWidget || !bridgeWidget || !swapWidget) return;

    walletWidget.addListener(ImtblCheckout.WalletEventType.CLOSE_WIDGET, () => {
      handleCloseWallet();
    });

    walletWidget.addListener(ImtblCheckout.WalletEventType.DISCONNECT_WALLET, async () => {
      handleConnectionClose();
    });

    walletWidget.addListener(ImtblCheckout.WalletEventType.NETWORK_SWITCH, (payload) => {
      handleNetworkSwitch(payload);
    });

    // wallet widget orchestration events

    walletWidget.addListener(ImtblCheckout.OrchestrationEventType.REQUEST_ONRAMP, () => {
      onRampWidget.mount(WALLET_WIDGETS_ROOT);
      onRampWidget.addListener(ImtblCheckout.OnRampEventType.CLOSE_WIDGET, () => {
        handleCloseWallet();
        handleOpenWallet();
        onRampWidget.removeListener(ImtblCheckout.OnRampEventType.CLOSE_WIDGET);
      });
    });

    walletWidget.addListener(ImtblCheckout.OrchestrationEventType.REQUEST_SWAP, () => {
      swapWidget.mount(WALLET_WIDGETS_ROOT);
      swapWidget.addListener(ImtblCheckout.SwapEventType.CLOSE_WIDGET, () => {
        handleCloseWallet();
        swapWidget.unmount();
        handleOpenWallet();
        swapWidget.removeListener(ImtblCheckout.SwapEventType.CLOSE_WIDGET);
      });
    });

    walletWidget.addListener(ImtblCheckout.OrchestrationEventType.REQUEST_BRIDGE, () => {
      bridgeWidget.mount(WALLET_WIDGETS_ROOT);
      bridgeWidget.addListener(ImtblCheckout.BridgeEventType.CLOSE_WIDGET, () => {
        handleCloseWallet();
        bridgeWidget.unmount();
        handleOpenWallet();
        bridgeWidget.removeListener(ImtblCheckout.BridgeEventType.CLOSE_WIDGET);
      });
    });

    return () => {
      walletWidget?.removeListener(ImtblCheckout.WalletEventType.CLOSE_WIDGET);
      walletWidget?.removeListener(ImtblCheckout.WalletEventType.DISCONNECT_WALLET);
      walletWidget?.removeListener(ImtblCheckout.WalletEventType.NETWORK_SWITCH);
    };
  }, [
    walletWidget,
    onRampWidget,
    bridgeWidget,
    swapWidget,
    handleOpenWallet,
    handleCloseWallet,
    handleConnectionClose,
    handleNetworkSwitch
  ]);

  useEffect(() => {
    // create wallet widget
    if (!factory || walletWidget) return;

    (async () => {
      try {
        const widget = factory.create(ImtblCheckout.WidgetType.WALLET);
        widgetsDispatch({ payload: { type: 'SET_WIDGET', widget: { walletWidget: widget } } });
      } catch (error) {
        notifyError(error);
      }
    })();
  }, [factory, widgetsDispatch, walletWidget]);

  return {
    walletWidget,
    networkInfo,
    walletOpen,
    walletProviderName,
    openWallet: handleOpenWallet,
    closeWallet: handleCloseWallet
  };
};
